import { showError } from '@components/app-error';
import { Button } from '@components/buttons';
import { PaymentType, PriceRow, PurchaseRow } from 'server/types';
import { rpx } from 'client/lib/rpx-client';
import { useState, useEffect } from 'preact/hooks';
import { useAuth } from 'client/lib/auth';
import { intl, useIntl } from 'shared/intl/use-intl';
import { isPauseActive } from 'shared/payments/types';
import * as pmtfmt from 'shared/payments/fmt';
import { aipAnchorPrice } from 'shared/mirasee-consts';

const store = rpx.subscriptionStatus;
const paymentsStore = rpx.payments;

export const planDescription = (paymentType: PaymentType) =>
  paymentType === 'paymentplan' ? intl('plan') : intl('subscription');

type CancelReason =
  | 'not-using'
  | 'too-expensive'
  | 'missing-features'
  | 'switching'
  | 'temporary'
  | 'other';

function cancelReasons(
  rate: string,
  anchor?: string,
): { value: CancelReason; label: string; coachingMessage: string }[] {
  return [
    {
      value: 'not-using',
      label: "I haven't been using it enough",
      coachingMessage:
        "We totally get it \u2014 life gets busy. But here's the thing... sometimes all it takes is one conversation to find the right starting point. Our coaches can help you identify the specific resources and trainings that'll move the needle for your business right now.",
    },
    {
      value: 'too-expensive',
      label: "It's too expensive right now",
      coachingMessage:
        "We understand \u2014 budgets shift. Before you go, a quick call with our team could help you get more value from your membership. We can point you to the resources that'll have the biggest impact on your business, so every dollar counts.",
    },
    {
      value: 'missing-features',
      label: "I'm not finding what I need",
      coachingMessage:
        "We'd love to hear what you were hoping to find. Our coaches know the full library inside and out \u2014 there may be resources that are a perfect fit that you haven't discovered yet. And your feedback directly shapes what we build next.",
    },
    {
      value: 'temporary',
      label: 'I need a break but may come back',
      coachingMessage: anchor
        ? `Before you step away, one thing worth knowing: your ${rate} founding rate is locked in only while your membership stays active. Membership now runs ${anchor}/month, so if you cancel and come back later, you'd rejoin at the new rate. A pause keeps your ${rate} rate and gives you the breathing room \u2014 and if there are resources you haven't explored yet, a quick call with a coach can point you at the ones most likely to move your business forward.`
        : `Before you step away, one thing worth knowing: a pause keeps your ${rate} rate and gives you the breathing room \u2014 and if there are resources you haven't explored yet, a quick call with a coach can point you at the ones most likely to move your business forward.`,
    },
    {
      value: 'switching',
      label: "I'm exploring other options",
      coachingMessage:
        "We'd love a chance to chat before you go. AI Playground combines live coaching, hands-on AI workshops, business-building courses, and a supportive community \u2014 all in one membership. A quick conversation can help clarify whether this is the right fit for where you are right now.",
    },
    {
      value: 'other',
      label: 'Something else',
      coachingMessage:
        "We'd really love to understand what would make this work for you. A quick conversation with one of our coaches is often all it takes to find the right path forward.",
    },
  ];
}

type CancelStep = 'reason' | 'coaching' | 'pause' | 'confirm' | 'processing' | 'paused' | 'done';

type NextMeeting = {
  title: string;
  scheduledAt: string;
  description: string;
};

function MeetingCard({ meeting }: { meeting: NextMeeting }) {
  return (
    <div style="border-left: 3px solid #4f46e5; padding: 10px 14px; background: #f9fafb; border-radius: 0 8px 8px 0">
      <p style="font-size: 11px; font-weight: 600; color: #6b7280; margin: 0 0 2px; text-transform: uppercase; letter-spacing: 0.5px">
        Coming up next
      </p>
      <p style="font-size: 14px; font-weight: 600; color: #111827; margin: 0">{meeting.title}</p>
      <p style="font-size: 13px; color: #6b7280; margin: 2px 0 0">
        {new Date(meeting.scheduledAt).toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}
      </p>
    </div>
  );
}

function CancelModal({
  purchase,
  price,
  courseId,
  onClose,
  onCanceled,
  onPaused,
}: {
  purchase: Pick<PurchaseRow, 'id'>;
  price: Pick<PriceRow, 'paymentType' | 'priceInCents' | 'currency'>;
  courseId?: UUID;
  onClose(): void;
  onCanceled(periodEndDate?: string): void;
  onPaused(pausedUntil: string): void;
}) {
  const plan = planDescription(price.paymentType);
  const [step, setStep] = useState<CancelStep>('reason');
  const [reason, setReason] = useState<CancelReason | undefined>();
  const [notes, setNotes] = useState('');
  const [nextMeeting, setNextMeeting] = useState<NextMeeting | undefined>();
  const [pausedUntil, setPausedUntil] = useState<string | undefined>();
  const isSubscription = price.paymentType === 'subscription';

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && step !== 'processing') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [step]);

  useEffect(() => {
    if (courseId) {
      store
        .getNextUpcomingMeeting({ courseId })
        .then(setNextMeeting)
        .catch(() => {});
    }
  }, [courseId]);

  // The member's own rate and the anchor for their currency, so the copy never
  // quotes a US figure at someone paying in Canadian dollars.
  const rate = `${pmtfmt.price(price)}/month`;
  const anchorPrice = aipAnchorPrice(price.currency);
  const anchor = anchorPrice ? pmtfmt.price(anchorPrice) : undefined;
  const reasons = cancelReasons(rate, anchor);
  const selectedReason = reasons.find((r) => r.value === reason);
  // Call titles lead with their date ("[October 13, 2026] Offer Diagnosis"); the
  // date already shows beside the title, so drop the bracketed prefix.
  const topic = nextMeeting?.title.replace(/^\s*\[[^\]]*\]\s*/, '');
  const meetingWithTopic = nextMeeting && topic ? { ...nextMeeting, title: topic } : undefined;

  async function handleConfirmCancel() {
    setStep('processing');
    try {
      const result = await store.cancelPurchase({
        purchaseId: purchase.id,
        courseId,
        reason,
        notes: notes || undefined,
      });
      onCanceled(result?.periodEndDate);
      setStep('done');
    } catch (err) {
      showError(err);
      setStep('confirm');
    }
  }

  async function handlePause() {
    setStep('processing');
    try {
      const result = await store.pauseSubscription({
        purchaseId: purchase.id,
        courseId,
        reason,
        notes: notes || undefined,
      });
      setPausedUntil(result.pausedUntil);
      setStep('paused');
    } catch (err) {
      showError(err);
      setStep('pause');
    }
  }

  return (
    <div
      style="position: fixed; inset: 0; z-index: 9999; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.5); padding: 20px"
      onClick={(e) => {
        if (e.target === e.currentTarget && step !== 'processing') {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
    >
      <div style="background: #fff; border-radius: 16px; max-width: 480px; width: 100%; box-shadow: 0 8px 32px rgba(0,0,0,0.2); overflow: hidden">
        {step === 'reason' && (
          <div style="padding: 28px">
            <h2 style="font-size: 20px; font-weight: 700; color: #111827; margin: 0 0 4px">
              Before you go...
            </h2>
            <p style="font-size: 14px; color: #6b7280; margin: 0 0 20px">
              We'd love to know why you're thinking about leaving. Your feedback helps us make AI
              Playground better for everyone.
            </p>
            <div style="display: flex; flex-direction: column; gap: 8px">
              {reasons.map((r) => (
                <label
                  key={r.value}
                  style={`display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-radius: 10px; border: 1.5px solid ${reason === r.value ? '#4f46e5' : '#e5e7eb'}; cursor: pointer; transition: all 0.15s; background: ${reason === r.value ? '#eef2ff' : '#fff'}`}
                >
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={() => setReason(r.value)}
                    style="accent-color: #4f46e5"
                  />
                  <span style="font-size: 14px; color: #374151">{r.label}</span>
                </label>
              ))}
            </div>
            {reason === 'other' && (
              <textarea
                value={notes}
                onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
                placeholder="Tell us more (optional)"
                style="width: 100%; margin-top: 12px; padding: 10px 12px; border: 1.5px solid #e5e7eb; border-radius: 10px; font-size: 14px; color: #374151; resize: vertical; min-height: 60px; font-family: inherit"
              />
            )}
            <div style="display: flex; gap: 12px; margin-top: 24px">
              <button
                type="button"
                onClick={onClose}
                style="flex: 1; padding: 12px; border: 1px solid #d1d5db; border-radius: 10px; background: #fff; color: #374151; font-size: 14px; font-weight: 600; cursor: pointer"
              >
                Keep my membership
              </button>
              <button
                type="button"
                onClick={() => setStep('coaching')}
                disabled={!reason}
                style={`flex: 1; padding: 12px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; ${reason ? 'background: #ef4444; color: #fff' : 'background: #e5e7eb; color: #9ca3af; cursor: not-allowed'}`}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 'coaching' && selectedReason && (
          <div style="padding: 28px">
            <h2 style="font-size: 20px; font-weight: 700; color: #111827; margin: 0 0 4px">
              Let's find the right path for you
            </h2>
            <p style="font-size: 14px; color: #6b7280; margin: 0 0 16px">
              Before you make a final decision, we'd love to help.
            </p>
            <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; padding: 16px; margin-bottom: 20px">
              <p style="font-size: 13px; font-weight: 600; color: #1e40af; margin: 0 0 8px">
                From the Mirasee team
              </p>
              <p style="font-size: 14px; color: #1e40af; margin: 0; line-height: 1.6">
                {selectedReason.coachingMessage}
              </p>
            </div>
            <div style="display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px">
              <a
                href="https://book-with-jim.youcanbook.me/"
                target="_blank"
                rel="noopener noreferrer"
                style="display: inline-flex; align-items: center; justify-content: center; gap: 8px; font-size: 14px; font-weight: 600; color: #fff; background: #4f46e5; padding: 12px 16px; border-radius: 10px; text-decoration: none; cursor: pointer"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                </svg>
                Book a free coaching call
              </a>
              <button
                type="button"
                onClick={onClose}
                style="padding: 12px 16px; border: 1px solid #d1d5db; border-radius: 10px; background: #fff; color: #374151; font-size: 14px; font-weight: 600; cursor: pointer"
              >
                I'll stick around!
              </button>
            </div>
            <div style="text-align: right">
              <button
                type="button"
                onClick={() => setStep(isSubscription ? 'pause' : 'confirm')}
                style="border: none; background: none; color: #9ca3af; font-size: 13px; cursor: pointer; text-decoration: underline; padding: 0"
              >
                Skip this step
              </button>
            </div>
          </div>
        )}

        {step === 'pause' && (
          <div style="padding: 28px">
            <h2 style="font-size: 20px; font-weight: 700; color: #111827; margin: 0 0 4px">
              How about a pause instead?
            </h2>
            <p style="font-size: 14px; color: #6b7280; margin: 0 0 16px; line-height: 1.6">
              Your membership stays active, billing takes a break for two months, and everything
              picks right back up automatically. Your community connections and progress will be
              waiting for you.
            </p>
            {meetingWithTopic && (
              <div style="margin-bottom: 16px">
                <MeetingCard meeting={meetingWithTopic} />
              </div>
            )}
            <div style="display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px">
              <button
                type="button"
                onClick={handlePause}
                style="display: inline-flex; align-items: center; justify-content: center; gap: 6px; font-size: 14px; font-weight: 600; color: #fff; background: #4f46e5; padding: 12px 16px; border-radius: 10px; border: none; cursor: pointer"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
                Pause for 2 months
              </button>
              <button
                type="button"
                onClick={onClose}
                style="padding: 12px 16px; border: 1px solid #d1d5db; border-radius: 10px; background: #fff; color: #374151; font-size: 14px; font-weight: 600; cursor: pointer"
              >
                I'll stick around!
              </button>
            </div>
            <div style="text-align: right">
              <button
                type="button"
                onClick={() => setStep('confirm')}
                style="border: none; background: none; color: #9ca3af; font-size: 13px; cursor: pointer; text-decoration: underline; padding: 0"
              >
                I still want to cancel
              </button>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div style="padding: 28px">
            <h2 style="font-size: 20px; font-weight: 700; color: #111827; margin: 0 0 8px">
              We'll miss you!
            </h2>
            {meetingWithTopic && (
              <div style="margin-bottom: 16px">
                <div style="border-left: 3px solid #f59e0b; padding: 10px 14px; background: #fffbeb; border-radius: 0 8px 8px 0">
                  <p style="font-size: 13px; color: #92400e; margin: 0; line-height: 1.5">
                    You'll miss trainings like <strong>{meetingWithTopic.title}</strong> on{' '}
                    {new Date(meetingWithTopic.scheduledAt).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}{' '}
                    {'\u2014'} and more every week.
                  </p>
                </div>
              </div>
            )}
            <p style="font-size: 14px; color: #6b7280; margin: 0 0 6px; line-height: 1.6">
              Here's what happens if you cancel:
            </p>
            <ul style="font-size: 14px; color: #374151; padding-left: 20px; margin: 0 0 20px; line-height: 1.8">
              <li>Your {plan} will be canceled at the end of your current billing period</li>
              <li>
                You'll keep full access to everything — live courses, workshops, trainings, and
                community — until then
              </li>
              {anchor && (
                <li>
                  <strong>You'll lose your {rate} founding rate.</strong> Membership is now {anchor}
                  /month, so coming back later means rejoining at the new rate.
                </li>
              )}
              <li>Your progress and community connections won't be lost</li>
            </ul>
            <div style="display: flex; gap: 12px">
              <button
                type="button"
                onClick={() => setStep(isSubscription ? 'pause' : 'coaching')}
                style="flex: 1; padding: 12px; border: 1px solid #d1d5db; border-radius: 10px; background: #fff; color: #374151; font-size: 14px; font-weight: 600; cursor: pointer"
              >
                Go back
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                style="flex: 1; padding: 12px; border: none; border-radius: 10px; background: #ef4444; color: #fff; font-size: 14px; font-weight: 600; cursor: pointer"
              >
                Cancel {plan}
              </button>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div style="padding: 48px 28px; text-align: center">
            <div style="display: inline-block; width: 32px; height: 32px; border: 3px solid #e5e7eb; border-top-color: #4f46e5; border-radius: 50%; animation: spin 0.8s linear infinite; margin-bottom: 16px" />
            <p style="font-size: 15px; color: #374151; font-weight: 500; margin: 0">
              Processing...
            </p>
            <style
              dangerouslySetInnerHTML={{
                __html: '@keyframes spin { to { transform: rotate(360deg) } }',
              }}
            />
          </div>
        )}

        {step === 'paused' && (
          <div style="padding: 28px; text-align: center">
            <div style="margin-bottom: 16px">
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                style="display: inline-block"
              >
                <circle cx="24" cy="24" r="24" fill="#eff6ff" />
                <rect x="17" y="15" width="5" height="18" rx="1.5" fill="#4f46e5" />
                <rect x="26" y="15" width="5" height="18" rx="1.5" fill="#4f46e5" />
              </svg>
            </div>
            <h2 style="font-size: 20px; font-weight: 700; color: #111827; margin: 0 0 8px">
              You're all set!
            </h2>
            <p style="font-size: 14px; color: #6b7280; margin: 0 0 20px; line-height: 1.6">
              Your billing is paused, but you still have full access to everything inside AI
              Playground — live courses, trainings, community, and support. Your membership will
              pick right back up on{' '}
              <strong>
                {pausedUntil
                  ? new Date(pausedUntil).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : 'next month'}
              </strong>
              .
            </p>
            <button
              type="button"
              onClick={() => {
                onPaused(pausedUntil || '');
                onClose();
              }}
              style="padding: 12px 32px; border: none; border-radius: 10px; background: #4f46e5; color: #fff; font-size: 14px; font-weight: 600; cursor: pointer"
            >
              Got it
            </button>
          </div>
        )}

        {step === 'done' && (
          <div style="padding: 28px; text-align: center">
            <div style="margin-bottom: 16px">
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                style="display: inline-block"
              >
                <circle cx="24" cy="24" r="24" fill="#f0fdf4" />
                <path
                  d="M15 24.5l6 6 12-12"
                  stroke="#16a34a"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <h2 style="font-size: 20px; font-weight: 700; color: #111827; margin: 0 0 8px">
              {planDescription(price.paymentType).charAt(0).toUpperCase() +
                planDescription(price.paymentType).slice(1)}{' '}
              canceled
            </h2>
            <p style="font-size: 14px; color: #6b7280; margin: 0 0 20px; line-height: 1.6">
              You'll continue to have full access to everything inside AI Playground — live courses,
              workshops, trainings, and community — until the end of your current billing period.
              You're always welcome to rejoin, and we hope to see you again!
            </p>
            <button
              type="button"
              onClick={onClose}
              style="padding: 12px 32px; border: none; border-radius: 10px; background: #4f46e5; color: #fff; font-size: 14px; font-weight: 600; cursor: pointer"
            >
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

async function resumePlan(props: {
  purchase: Pick<PurchaseRow, 'id'>;
  price: Pick<PriceRow, 'paymentType' | 'priceInCents' | 'currency'>;
}) {
  try {
    const plan = planDescription(props.price.paymentType);

    const previewData = await paymentsStore.previewResumeSubscription({
      purchaseId: props.purchase.id,
    });

    const nextChargeText = previewData.chargesImmediately
      ? 'You will be charged immediately.'
      : `Next charge: ${new Date(previewData.nextChargeDate).toLocaleDateString()}`;

    const paymentPlanText = previewData.paymentsRequired
      ? ` You have made ${previewData.paymentsMade} of ${previewData.paymentsRequired} payments.`
      : '';

    const { showDialog } = await import('@components/dialog');
    const confirmed = await showDialog({
      children: `Resume this ${plan}? ${nextChargeText}${paymentPlanText}`,
      title: intl('Resume {plan:string}', {
        plan,
      }),
      confirmButtonText: intl('Resume {plan:string}', {
        plan,
      }),
      mode: 'ok',
      cancelButtonText: intl('Cancel'),
    });

    if (!confirmed) {
      return;
    }

    await paymentsStore.resumeSubscription({ purchaseId: props.purchase.id });
    location.reload();
    await new Promise(() => {});
  } catch (err) {
    showError(err);
  }
}

export function SubscriptionStatus(props: {
  purchase: Pick<PurchaseRow, 'id' | 'disableAccessWhenCanceled' | 'status' | 'pausedUntil'>;
  price: Pick<PriceRow, 'paymentType' | 'allowSelfCancel' | 'priceInCents' | 'currency'>;
  courseId?: UUID;
  allowChanges: boolean;
  asSeller: boolean;
  guideEmail?: string;
}) {
  const { purchase, price, allowChanges, asSeller } = props;
  const { paymentType } = price;
  const { status } = purchase;
  const [isResuming, setIsResuming] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [canceledStatus, setCanceledStatus] = useState<string | undefined>();
  const [localPausedUntil, setLocalPausedUntil] = useState<string | undefined>(
    purchase.pausedUntil ? new Date(purchase.pausedUntil).toISOString() : undefined,
  );
  const auth = useAuth();
  const level = auth.config.coreUser?.level || auth.user?.level;
  const intl = useIntl();
  const isActiveSubscriptionLike = allowChanges && status === 'active';
  const isCanceledSubscriptionLike =
    allowChanges &&
    ['subscription', 'paymentplan'].includes(paymentType) &&
    ['canceled', 'complete'].includes(canceledStatus || status) &&
    level === 'superadmin';
  const mayCancel = paymentType === 'subscription' || asSeller || price.allowSelfCancel;
  const contactText = intl.split('<>Contact your guide</> for questions.');
  const isPaused = !!localPausedUntil && isPauseActive(localPausedUntil);

  const displayStatus = canceledStatus || (isPaused ? 'paused' : status);

  const handleResume = async () => {
    setIsResuming(true);
    try {
      await resumePlan(props);
    } finally {
      setIsResuming(false);
    }
  };

  return (
    <div class="flex gap-2 flex-wrap">
      <span>
        {isPaused ? (
          <>
            This subscription is paused until{' '}
            {new Date(localPausedUntil).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
            . Billing will resume automatically.
          </>
        ) : (
          intl('This {plan:string} is {status:string}.', {
            plan: planDescription(paymentType),
            status: displayStatus,
          })
        )}
      </span>
      {!allowChanges && (
        <span>
          Contact{' '}
          <a href="mailto:billing@ruzuku.com" class="text-indigo-600 underline">
            billing
          </a>{' '}
          for details.
        </span>
      )}
      {isActiveSubscriptionLike && !canceledStatus && !isPaused && mayCancel && (
        <Button
          class="border border-red-200 text-red-500 rounded-sm px-2 hover:bg-red-50"
          onClick={() => setShowCancelModal(true)}
        >
          {intl('Cancel {plan:string}', {
            plan: planDescription(paymentType),
          })}
        </Button>
      )}
      {(isCanceledSubscriptionLike || canceledStatus === 'canceling') && level === 'superadmin' && (
        <Button
          class="border border-green-200 text-green-600 rounded-sm px-2 hover:bg-green-50"
          onClick={handleResume}
          isLoading={isResuming}
        >
          {intl('Resume {plan:string}', {
            plan: planDescription(paymentType),
          })}
        </Button>
      )}
      {isActiveSubscriptionLike && !mayCancel && !!props.guideEmail && (
        <span>
          {contactText[0]} <a href={`mailto:${props.guideEmail}`}>{contactText[1]}</a>{' '}
          {contactText[2]}
        </span>
      )}
      {showCancelModal && (
        <CancelModal
          purchase={purchase}
          price={price}
          courseId={props.courseId}
          onClose={() => setShowCancelModal(false)}
          onCanceled={() => {
            setCanceledStatus('canceling');
            setShowCancelModal(false);
          }}
          onPaused={(until) => {
            setLocalPausedUntil(until);
            setShowCancelModal(false);
          }}
        />
      )}
    </div>
  );
}
